"use strict";
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveGroupIdForCategory = exports.planMetricsSchemaDiff = exports.buildMetricsSchemaFromApi = exports.METRICS_DRAFT_CATEGORY_PREFIX = exports.METRICS_GROUP_CATEGORY_PREFIX = exports.METRICS_UNASSIGNED_CATEGORY_ID = void 0;
/** Sentinel id for the synthetic bucket holding ungrouped elements. */
exports.METRICS_UNASSIGNED_CATEGORY_ID = 'unassigned';
/** Prefix for category ids that map onto a real backend group. */
exports.METRICS_GROUP_CATEGORY_PREFIX = 'group:';
/** Prefix for category ids that exist only in the draft (unsaved). */
exports.METRICS_DRAFT_CATEGORY_PREFIX = 'draft:';
/**
 * Convert a backend element type group into the UI-side
 * `WsMetricsCategory.kind`. Anything not flagged as AI-generated is
 * treated as curator-controlled (`predefined`).
 */
var toKind = function (origin) {
    return origin === 'AI Generated' ? 'ai-generated' : 'predefined';
};
var elementToProperty = function (element) {
    var _a, _b, _c, _d;
    // Coerce a use-case ref into the canonical `{ id, title }` shape.
    // The legacy backend used to return either a bare string id (in
    // `aliases[].useCases`) or `useCaseIds: string[]` at the element
    // root. Normalise both to `{ id, title }` so the rest of the UI
    // doesn't have to care which shape it got.
    var toRef = function (raw) {
        if (typeof raw === 'string') {
            return { id: raw, title: raw };
        }
        if (raw && typeof raw === 'object') {
            var obj = raw;
            var id = typeof obj.id === 'string' ? obj.id : '';
            if (!id) {
                return null;
            }
            var title = typeof obj.title === 'string' && obj.title.length > 0 ? obj.title : id;
            var workflow = typeof obj.workflow === 'string' && obj.workflow.trim().length > 0
                ? obj.workflow
                : undefined;
            return workflow ? { id: id, title: title, workflow: workflow } : { id: id, title: title };
        }
        return null;
    };
    // Tolerate both the new `{ text, useCases }` shape and the legacy
    // `string[]` shape so we don't crash if the user hits a page while
    // an older backend build is still serving the response (or while a
    // stale payload sits in the browser HTTP cache).
    var aliases = ((_a = element.aliases) !== null && _a !== void 0 ? _a : []).map(function (alias) {
        var _a;
        if (typeof alias === 'string') {
            return { text: alias, useCases: [] };
        }
        var useCases = Array.isArray(alias.useCases)
            ? alias.useCases
                .map(toRef)
                .filter(function (ref) { return ref !== null; })
            : [];
        return {
            text: (_a = alias.text) !== null && _a !== void 0 ? _a : '',
            useCases: useCases,
        };
    });
    // Roll up element-level use cases so the right-panel "Used in N
    // use cases" pill and its callout work without an extra round-
    // trip. We prefer the top-level `useCases` (or legacy `useCaseIds`)
    // from the API since it covers every mention — including those
    // whose raw text didn't normalise to any alias key — and fall back
    // to aggregating from aliases when neither is present.
    var seen = new Map();
    var addRef = function (ref) {
        if (!ref || seen.has(ref.id)) {
            return;
        }
        seen.set(ref.id, ref);
    };
    if (Array.isArray(element.useCases)) {
        for (var _i = 0, _e = element.useCases; _i < _e.length; _i++) {
            var raw = _e[_i];
            addRef(toRef(raw));
        }
    }
    else if (Array.isArray(element.useCaseIds)) {
        for (var _f = 0, _g = element.useCaseIds; _f < _g.length; _f++) {
            var id = _g[_f];
            addRef({ id: id, title: id });
        }
    }
    else {
        for (var _h = 0, aliases_1 = aliases; _h < aliases_1.length; _h++) {
            var alias = aliases_1[_h];
            for (var _j = 0, _k = alias.useCases; _j < _k.length; _j++) {
                var ref = _k[_j];
                addRef(ref);
            }
        }
    }
    var useCases = Array.from(seen.values()).sort(function (a, b) {
        return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    });
    return {
        id: element.id,
        isActive: (_b = element.isActive) !== null && _b !== void 0 ? _b : true,
        isEnabled: (_c = element.isEnabled) !== null && _c !== void 0 ? _c : true,
        isReady: (_d = element.isReady) !== null && _d !== void 0 ? _d : false,
        name: element.canonicalName,
        internalName: element.naturalKey,
        system: 'AI Hub',
        useCases: useCases,
        aliases: aliases,
    };
};
/** Map raw API rows into the schema shape the Metrics UI expects. */
exports.buildMetricsSchemaFromApi = function (groups, elements) {
    var byGroupId = new Map();
    for (var _i = 0, groups_1 = groups; _i < groups_1.length; _i++) {
        var group = groups_1[_i];
        byGroupId.set(group.id, {
            id: "" + exports.METRICS_GROUP_CATEGORY_PREFIX + group.id,
            name: group.name,
            kind: toKind(group.origin),
            properties: [],
        });
    }
    var unassigned = {
        id: exports.METRICS_UNASSIGNED_CATEGORY_ID,
        name: 'Unassigned',
        kind: 'predefined',
        properties: [],
    };
    for (var _a = 0, elements_1 = elements; _a < elements_1.length; _a++) {
        var element = elements_1[_a];
        var property = elementToProperty(element);
        if (element.groupId === null || element.groupId === undefined) {
            unassigned.properties.push(property);
            continue;
        }
        var target = byGroupId.get(element.groupId);
        if (target) {
            target.properties.push(property);
        }
        else {
            // Defensive fallback: element points at a group we did not load
            // (race with a group deletion). Surface it as unassigned so the
            // admin can re-bucket it manually.
            unassigned.properties.push(property);
        }
    }
    // Unassigned bucket appears first so admins can quickly triage.
    return __spreadArrays([unassigned], Array.from(byGroupId.values()));
};
var indexCategoryByPropertyId = function (schema) {
    var index = new Map();
    for (var _i = 0, schema_1 = schema; _i < schema_1.length; _i++) {
        var category = schema_1[_i];
        for (var _a = 0, _b = category.properties; _a < _b.length; _a++) {
            var property = _b[_a];
            index.set(property.id, category.id);
        }
    }
    return index;
};
var parseGroupCategoryId = function (id) {
    if (!id.startsWith(exports.METRICS_GROUP_CATEGORY_PREFIX)) {
        return null;
    }
    var tail = id.slice(exports.METRICS_GROUP_CATEGORY_PREFIX.length);
    var parsed = Number(tail);
    return Number.isInteger(parsed) ? parsed : null;
};
/**
 * Compare draft vs saved schema and produce the minimal list of
 * backend mutations the save action must run. The list is grouped by
 * operation type so the executor can sequence creations before
 * element moves (a moved element may target a brand-new group).
 */
exports.planMetricsSchemaDiff = function (draft, saved) {
    var savedById = new Map(saved.map(function (category) { return [category.id, category]; }));
    var draftById = new Map(draft.map(function (category) { return [category.id, category]; }));
    var groupsToCreate = [];
    var groupsToRename = [];
    var groupsToDelete = [];
    for (var _i = 0, draft_1 = draft; _i < draft_1.length; _i++) {
        var draftCategory = draft_1[_i];
        if (draftCategory.id === exports.METRICS_UNASSIGNED_CATEGORY_ID) {
            continue;
        }
        var savedCategory = savedById.get(draftCategory.id);
        if (!savedCategory) {
            groupsToCreate.push({
                draftCategoryId: draftCategory.id,
                name: draftCategory.name,
            });
            continue;
        }
        if (savedCategory.name !== draftCategory.name) {
            var groupId = parseGroupCategoryId(draftCategory.id);
            if (groupId !== null) {
                groupsToRename.push({ groupId: groupId, name: draftCategory.name });
            }
        }
    }
    for (var _a = 0, saved_1 = saved; _a < saved_1.length; _a++) {
        var savedCategory = saved_1[_a];
        if (savedCategory.id === exports.METRICS_UNASSIGNED_CATEGORY_ID) {
            continue;
        }
        if (!draftById.has(savedCategory.id)) {
            var groupId = parseGroupCategoryId(savedCategory.id);
            if (groupId !== null) {
                groupsToDelete.push(groupId);
            }
        }
    }
    var savedPropertyIndex = indexCategoryByPropertyId(saved);
    var elementMoves = [];
    for (var _b = 0, draft_2 = draft; _b < draft_2.length; _b++) {
        var draftCategory = draft_2[_b];
        for (var _c = 0, _d = draftCategory.properties; _c < _d.length; _c++) {
            var property = _d[_c];
            var previousCategoryId = savedPropertyIndex.get(property.id);
            if (previousCategoryId !== draftCategory.id) {
                elementMoves.push({
                    elementId: property.id,
                    targetCategoryId: draftCategory.id,
                });
            }
        }
    }
    return { groupsToCreate: groupsToCreate, groupsToRename: groupsToRename, groupsToDelete: groupsToDelete, elementMoves: elementMoves };
};
/**
 * Resolve a category id (potentially a draft sentinel) into the
 * backend `groupId | null` value the assign endpoint expects. The
 * caller passes a map from draft category id → freshly created group
 * id so newly added groups can receive their first elements in the
 * same save.
 */
exports.resolveGroupIdForCategory = function (categoryId, draftToBackendId) {
    var _a;
    if (categoryId === exports.METRICS_UNASSIGNED_CATEGORY_ID) {
        return null;
    }
    var fromDraft = draftToBackendId.get(categoryId);
    if (typeof fromDraft === 'number') {
        return fromDraft;
    }
    return (_a = parseGroupCategoryId(categoryId)) !== null && _a !== void 0 ? _a : undefined;
};
